import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Popconfirm, Space } from 'antd';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import useUserProfile from '../../useUserProfile';
import { Select, Spin } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';


const UnitBudgetAdminPanel: React.FC = () => {
    const [budgets, setBudgets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingBudget, setEditingBudget] = useState<any | null>(null);
    const [form] = Form.useForm();
    const { instance, accounts } = useMsal();
    const { requestProfileData } = useUserProfile();
    const [UserDetails, setUserDetails] = useState<{ id: string, displayName: string, email: string }[]>([]);
    const [searching, setSearching] = useState(false);
    const [managerOptions, setManagerOptions] = useState<any[]>([]);
    const [searchText, setSearchText] = useState('');

    const fetchBudgets = async () => {
        setLoading(true);
        try {
            const token = await fetchToken();

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/budget/unit`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBudgets(res.data);
        } catch(error) {
            console.log('Failed to fetch unit budgets', error);
            message.error('Failed to fetch unit budgets');
        }
        setLoading(false);
    };
    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                await requestProfileData();
                const userMail = localStorage.getItem("UserMail");
                const userName = localStorage.getItem("UserName");
                const userId = localStorage.getItem("UserId");
                setUserDetails([{ id: userId || '', displayName: userName || 'Default Nominator', email: userMail || '' }]);

            } catch (error) {
                console.error('Error fetching profile data:', error);
            }
        };

        fetchProfileData();
    }, [requestProfileData]);
    useEffect(() => {
        fetchBudgets();
    }, []);

    const handleAdd = () => {
        setEditingBudget(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record: any) => {
        setEditingBudget(record);

        // Try to find the manager in options, or add it if not present
        const existing = managerOptions.find((u) => u.id === record.unitHeadUserId);
        if (!existing) {
            setManagerOptions((prev) => [
                ...prev,
                {
                    id: record.unitHeadUserId,
                    displayName: record.unitHeadName,
                    email: record.unitHeadEmail || '', // If you have email in your record
                },
            ]);
        }

        form.setFieldsValue({
            ...record,
            unitHeadUserId: record.unitHeadUserId, // This will match the value in Select.Option
            unitHeadName: record.unitHeadName,
        });
        setModalVisible(true);
    };
    const fetchToken = async () => {
        const res = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        });
        return res.idToken;
    };
    const handleDelete = async (id: string) => {
        try {
            const token = await fetchToken();

            const res = await axios.delete(`${process.env.REACT_APP_API_URL}/api/budget/unit/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // await axios.delete(`${apiBase}/${id}`);
            message.success('Deleted successfully');
            fetchBudgets();
        } catch {
            message.error('Delete failed');
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const updateFormData = {
                ...values,
                updatedBy: UserDetails[0].id,
            }
            const token = await fetchToken();
            if (editingBudget) {
                // Update
                await axios.put(`${process.env.REACT_APP_API_URL}/api/budget/unit`, { ...editingBudget, ...updateFormData }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success('Updated successfully');
            } else {
                // Create
                const createFormData = {
                    ...values,
                    createdBy: UserDetails[0].id,
                }
                await axios.post(`${process.env.REACT_APP_API_URL}/api/budget/unit`, createFormData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success('Created successfully');
            }
            setModalVisible(false);
            fetchBudgets();
        } catch (err: any) {
            // Form validation errors are shown on the fields; show the API's reason for anything else
            // (e.g. Awards Used above Total GEM Awards).
            const detail = err?.response?.data;
            if (detail) {
                message.error(typeof detail === 'string' ? detail : 'Save failed');
            }
        }
    };

    // Search managers by name or GUID
    const fetchManagers = async (term: string) => {
        if (!term || term.length < 3) {
            setManagerOptions([]);
            return;
        }
        setSearching(true);
        try {
            const token = await fetchToken();
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/search?search=${encodeURIComponent(term)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setManagerOptions(res.data);
        } catch {
            setManagerOptions([]);
        }
        setSearching(false);
    };

    // Balance = Total GEM Awards - Awards Used. Can be negative when a unit head has
    // approved more GEM awards than allowed. Totals can be fractional, so round to 2 dp.
    const balanceOf = (budget: any): number =>
        Math.round(((Number(budget.awardsCount) || 0) - (Number(budget.awardsUsed) || 0)) * 100) / 100;

    // Export to Excel
    const exportToExcel = () => {
        const filteredData = budgets.filter(b =>
            b.unitHeadName?.toLowerCase().includes(searchText.toLowerCase())
        );
        
        const dataToExport = filteredData.map(budget => ({
            'Unit Head Name': budget.unitHeadName || '-',
            'Total GEM Awards': budget.awardsCount,
            'Awards Used': budget.awardsUsed,
            'Span': budget.span || '-',
            'Balance': balanceOf(budget),
            'Comments': budget.comments || '-'
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Unit Budgets');
        
        // Generate file name with current date
        const fileName = `Unit_Budgets_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        message.success('Excel file exported successfully');
    };

    const columns = [
        { title: 'Unit Head Name', dataIndex: 'unitHeadName', key: 'unitHeadName' },
        { title: 'Total GEM Awards', dataIndex: 'awardsCount', key: 'awardsCount' },
        { title: 'Awards Used', dataIndex: 'awardsUsed', key: 'awardsUsed' },
        { title: 'Span', dataIndex: 'span', key: 'span' },
        {
            title: 'Balance',
            key: 'balance',
            sorter: (a: any, b: any) => balanceOf(a) - balanceOf(b),
            render: (_: any, record: any) => {
                const balance = balanceOf(record);
                return <span style={{ color: balance < 0 ? '#d03b3b' : undefined, fontWeight: 600 }}>{balance}</span>;
            },
        },
        { title: 'Comments', dataIndex: 'comments', key: 'comments' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => (
                <>
                    <Button type="link" onClick={() => handleEdit(record)}>Edit</Button>
                    <Popconfirm
                        title="Are you sure to delete this budget?"
                        onConfirm={() => handleDelete(record.unitBudgetId)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger>Delete</Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    const filteredBudgets = budgets.filter(b =>
        b.unitHeadName?.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={handleAdd}>
                    Add Unit Budget
                </Button>
                <Button 
                    type="default" 
                    icon={<DownloadOutlined />} 
                    onClick={exportToExcel}
                    disabled={filteredBudgets.length === 0}
                >
                    Export to Excel
                </Button>
            </Space>
            <Input
                placeholder="Search by Unit Head Name"
                value={searchText}
                prefix={<SearchOutlined />}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 300, marginBottom: 16, marginLeft: 16 }}
                allowClear
            />
            <Table
                dataSource={filteredBudgets}
                columns={columns}
                rowKey="unitBudgetId"
                loading={loading}
            />
            <Modal
                title={editingBudget ? 'Edit Unit Budget' : 'Add Unit Budget'}
                visible={modalVisible}
                onOk={handleModalOk}
                onCancel={() => setModalVisible(false)}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="unitHeadUserId"
                        label="Unit Head"
                        rules={[{ required: true, message: 'Please select a unit head' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Search by name, email, or GUID"
                            filterOption={false}
                            notFoundContent={searching ? <Spin size="small" /> : null}
                            onSearch={fetchManagers}
                            onChange={(_, option: any) => {
                                form.setFieldsValue({ unitHeadName: option?.userdisplayname || '' });
                            }}
                        >
                            {managerOptions.map((user: any) => (
                                <Select.Option
                                    key={user.id}
                                    value={user.id}
                                    userdisplayname={user.displayName}
                                >
                                    {user.displayName} ({user.email})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="unitHeadName" label="Unit Head Name" style={{ display: 'none' }}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="awardsCount" label="Total GEM Awards" rules={[{ required: true, type: 'number', min: 0 }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="awardsUsed"
                        label="Awards Used"
                        rules={[{ required: true, type: 'number', min: 0, message: 'Please enter a non-negative number' }]}
                    >
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="span" label="Span" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="comments" label="Comments">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UnitBudgetAdminPanel;